import { getReferenceString } from '@medplum/core';
import { Observation, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import {
  BODY_HEIGHT_LOINC_CODING,
  BODY_MASS_INDEX_LOINC_CODING,
  BODY_WEIGHT_LOINC_CODING,
} from '@bariendo/lib/codings';
import { calculateBMIFromObservations, observationWeightToKg } from '@bariendo/lib/bmi';
import dayjs from 'dayjs';

/** Weight values in {@link BMIData} are normalized to kilograms for display via kgToLbs. */
export interface BMIData {
  startingWeight?: { value: number; date: string };
  latestWeight?: { value: number; date: string };
  startingBMI?: { value: number; date: string };
  latestBMI?: { value: number; date: string };
  height?: { value: number };
}

export interface UseBMIDataResult {
  bmiData: BMIData;
  bmiDataLoading: boolean;
  refreshBMIData: () => Promise<void>;
}

function getObservationDate(observation: Observation): string {
  return observation.effectiveDateTime || observation.meta?.lastUpdated || '';
}

function hasValidSortDate(observation: Observation): boolean {
  const d = getObservationDate(observation);
  return d !== '' && dayjs(d).isValid();
}

function sortObservationsByDate(observations: Observation[]): Observation[] {
  return [...observations].filter(hasValidSortDate).sort((a, b) => {
    return dayjs(getObservationDate(a)).valueOf() - dayjs(getObservationDate(b)).valueOf();
  });
}

/** Sort by date and keep only rows we can convert to kg (unrelated to {@link sortObservationsByDate}'s date filter). */
function sortWeightObservationsByDate(observations: Observation[]): Observation[] {
  return sortObservationsByDate(observations).filter((obs) => observationWeightToKg(obs) !== undefined);
}

// lets convert the height to cm, so we can convert it easier to feet or inches  along the system
function convertToCm(observation: Observation): Observation {
  // If no valueQuantity or value, return as-is
  if (!observation.valueQuantity?.value || !observation.valueQuantity?.code) {
    return observation;
  }

  const code = observation.valueQuantity.code.toLowerCase();
  const value = observation.valueQuantity.value;

  // Check if conversion is needed
  if (code === 'cm') {
    // Already in centimeters
    return observation;
  }

  let convertedValue: number;

  if (code === 'in') {
    // Convert inches to centimeters: 1 inch = 2.54 cm
    convertedValue = value * 2.54;
  } else if (code === 'feet' || code === 'ft') {
    // Convert feet to centimeters: 1 foot = 30.48 cm
    convertedValue = value * 30.48;
  } else {
    // Unknown unit, return as-is
    return observation;
  }

  // Return new observation with converted value
  return {
    ...observation,
    valueQuantity: {
      ...observation.valueQuantity,
      value: convertedValue,
      unit: 'cm',
      code: 'cm',
      system: 'http://unitsofmeasure.org',
    },
  };
}

function weightObservationToKgEntry(obs: Observation): { value: number; date: string } | undefined {
  const kg = observationWeightToKg(obs);
  if (kg === undefined) {
    return undefined;
  }
  return { value: kg, date: getObservationDate(obs) };
}

function bmiObservationToEntry(obs: Observation): { value: number; date: string } | undefined {
  const v = obs.valueQuantity?.value;
  if (v === undefined) {
    return undefined;
  }
  return { value: v, date: getObservationDate(obs) };
}

function bmiFromWeight(
  weightObs: Observation | undefined,
  heightObs: Observation | undefined,
  weightEntry: { value: number; date: string } | undefined
): { value: number; date: string } | undefined {
  if (!weightObs || !heightObs || !weightEntry) {
    return undefined;
  }
  const computed = calculateBMIFromObservations(weightObs, heightObs);
  return computed === undefined ? undefined : { value: computed, date: weightEntry.date };
}

export function useBMIData(patient: Patient | undefined): UseBMIDataResult {
  const medplum = useMedplum();
  const [bmiData, setBmiData] = useState<BMIData>({});
  const [bmiDataLoading, setBmiDataLoading] = useState(false);

  const loadBMIData = useCallback(async () => {
    if (!patient?.id) {
      setBmiData({});
      setBmiDataLoading(false);
      return;
    }

    try {
      setBmiDataLoading(true);
      const patientRef = getReferenceString(patient);

      const allCodes = [
        `${BODY_WEIGHT_LOINC_CODING.system}|${BODY_WEIGHT_LOINC_CODING.code}`,
        `${BODY_MASS_INDEX_LOINC_CODING.system}|${BODY_MASS_INDEX_LOINC_CODING.code}`,
        `${BODY_HEIGHT_LOINC_CODING.system}|${BODY_HEIGHT_LOINC_CODING.code}`,
      ].join(',');

      const allObservations = await medplum.searchResources('Observation', {
        subject: patientRef,
        code: allCodes,
        _sort: 'date',
        _count: 1000,
      });

      const weightObservations = allObservations.filter(
        (obs) => obs.code?.coding?.some((c) => c.code === BODY_WEIGHT_LOINC_CODING.code) ?? false
      );
      const bmiObservations = allObservations.filter(
        (obs) => obs.code?.coding?.some((c) => c.code === BODY_MASS_INDEX_LOINC_CODING.code) ?? false
      );
      const rawHeightObservations = allObservations.filter(
        (obs) => obs.code?.coding?.some((c) => c.code === BODY_HEIGHT_LOINC_CODING.code) ?? false
      );

      const sortedWeights = sortWeightObservationsByDate(weightObservations);
      const earliestWeightObs = sortedWeights[0];
      const latestWeightObs = sortedWeights.length > 0 ? sortedWeights[sortedWeights.length - 1] : undefined;

      const startingWeight = earliestWeightObs ? weightObservationToKgEntry(earliestWeightObs) : undefined;
      const latestWeight = latestWeightObs ? weightObservationToKgEntry(latestWeightObs) : undefined;

      const heightSorted = sortObservationsByDate(rawHeightObservations).map(convertToCm);
      const heightWithValues = heightSorted.filter((o) => o.valueQuantity?.value !== undefined);
      const latestHeightObs = heightWithValues.length > 0 ? heightWithValues[heightWithValues.length - 1] : undefined;

      const height =
        latestHeightObs?.valueQuantity?.value !== undefined
          ? { value: latestHeightObs.valueQuantity.value }
          : undefined;

      // BMI follows the Weight row: computed from the same earliest/latest weight
      // and the latest height. Stored BMI Observations are only written by the
      // charted BMI questionnaire, so a weight entered any other way has no BMI
      // beside it; preferring stored BMI froze the row at the last charted value.
      // Stored BMI is the fallback for when weight or height is missing.
      const sortedBmi = sortObservationsByDate(bmiObservations).filter((obs) => obs.valueQuantity?.value !== undefined);
      const startingBMI =
        bmiFromWeight(earliestWeightObs, latestHeightObs, startingWeight) ??
        (sortedBmi.length > 0 ? bmiObservationToEntry(sortedBmi[0]) : undefined);
      const latestBMI =
        bmiFromWeight(latestWeightObs, latestHeightObs, latestWeight) ??
        (sortedBmi.length > 0 ? bmiObservationToEntry(sortedBmi[sortedBmi.length - 1]) : undefined);

      setBmiData({
        startingWeight,
        latestWeight,
        startingBMI,
        latestBMI,
        height,
      });
    } catch (_error) {
      setBmiData({});
    } finally {
      setBmiDataLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    loadBMIData();
  }, [loadBMIData]);

  return {
    bmiData,
    bmiDataLoading,
    refreshBMIData: loadBMIData,
  };
}
